/**
 * CurrencySwitcher - Client-side currency conversion for non-FR pages
 */
export class CurrencySwitcher {
  static STORAGE_KEY = "altimood_currency";
  static DEFAULT_CURRENCY = "EUR";
  static USD_RATE = 2;
  static CURRENCIES = ["EUR", "USD", "CAD", "GBP", "CHF", "AUD", "SEK", "NOK", "DKK", "JPY", "CNY", "INR"];
  static rates = { EUR: 1, USD: 2, CAD: 1.5, GBP: 0.85, CHF: 0.95, AUD: 1.65, SEK: 11, NOK: 11.5, DKK: 7.45, JPY: 160, CNY: 7.8, INR: 92 };
  static margin = 0.02;
  static currentCurrency = null;

  static async init() {
    await this.loadRates();
    this.currentCurrency = this.getSavedCurrency();
    this.updateAllPrices();
    this.bindToggle();
  }

  static async loadRates() {
    try {
      const response = await fetch("/exchange-rates.json");
      if (response.ok) {
        const data = await response.json();
        this.rates = data.rates || this.rates;
        this.margin = data.margin ?? this.margin;
      }
    } catch {
      // Use fallback rates
    }
  }

  static LOCALE_CURRENCY_MAP = {
    fr: "EUR",
    en: "USD",
    "en-CA": "CAD",
    "fr-CA": "CAD",
    "en-GB": "GBP",
    gd: "GBP",
    "de-CH": "CHF",
    "fr-CH": "CHF",
    "it-CH": "CHF",
    "en-AU": "AUD",
    sv: "SEK",
    no: "NOK",
    nb: "NOK",
    "nb-NO": "NOK",
    nn: "NOK",
    da: "DKK",
    "da-DK": "DKK",
    ja: "JPY",
    zh: "CNY",
    "zh-CN": "CNY",
    "zh-Hans": "CNY",
    hi: "INR",
    "en-IN": "INR",
  };

  static getSavedCurrency() {
    try {
      const saved = localStorage.getItem(this.STORAGE_KEY);
      if (saved && this.CURRENCIES.includes(saved)) {
        return saved;
      }
      const locale = document.documentElement.lang || "fr";
      return this.LOCALE_CURRENCY_MAP[locale] || this.DEFAULT_CURRENCY;
    } catch {
      const locale = document.documentElement.lang || "fr";
      return this.LOCALE_CURRENCY_MAP[locale] || this.DEFAULT_CURRENCY;
    }
  }

  static saveCurrency(currency) {
    try {
      localStorage.setItem(this.STORAGE_KEY, currency);
    } catch {
      // localStorage not available
    }
  }

  static switchTo(currency) {
    this.currentCurrency = currency;
    this.saveCurrency(currency);
    this.updateAllPrices();
    this.updateToggleState();
  }

  static formatPrice(amountEur, currency) {
    // USD uses a fixed selling price, including when the JSON is stale or unavailable.
    const rate = currency === "USD" ? this.USD_RATE : this.rates?.[currency] || 1;
    // Apply margin to EUR on non-FR pages (margin is already baked into other rates)
    const amount =
      currency === "EUR"
        ? Math.ceil(amountEur * (1 + this.margin))
        : Math.ceil(amountEur * rate);

    const formatOptions = {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    };

    if (currency === "EUR") {
      return new Intl.NumberFormat("fr-FR", formatOptions).format(amount) + " €";
    }

    const formats = {
      CAD: () => new Intl.NumberFormat("en-CA", formatOptions).format(amount) + " $ CA",
      GBP: () => "£" + new Intl.NumberFormat("en-GB", formatOptions).format(amount),
      CHF: () => new Intl.NumberFormat("de-CH", formatOptions).format(amount) + " CHF",
      AUD: () => "A$" + new Intl.NumberFormat("en-AU", formatOptions).format(amount),
      SEK: () => new Intl.NumberFormat("sv-SE", formatOptions).format(amount) + " kr",
      NOK: () => new Intl.NumberFormat("nb-NO", formatOptions).format(amount) + " kr",
      DKK: () => new Intl.NumberFormat("da-DK", formatOptions).format(amount) + " kr",
      JPY: () => "¥" + new Intl.NumberFormat("ja-JP", formatOptions).format(amount),
      CNY: () => "¥" + new Intl.NumberFormat("zh-CN", formatOptions).format(amount),
      INR: () => "₹" + new Intl.NumberFormat("en-IN", formatOptions).format(amount),
    };

    return formats[currency]?.() ?? "$" + new Intl.NumberFormat("en-US", formatOptions).format(amount);
  }

  static updateAllPrices() {
    const elements = document.querySelectorAll("[data-price-eur]");
    elements.forEach((el) => {
      const priceEur = parseInt(el.dataset.priceEur, 10);
      if (!isNaN(priceEur)) {
        el.textContent = this.formatPrice(priceEur, this.currentCurrency);
      }
    });
  }

  static bindToggle() {
    const switcher = document.querySelector("[data-currency-switcher]");
    if (!switcher) return;

    this.buildDropdownOptions(switcher);

    // Bind click events on currency options
    switcher.querySelectorAll("[data-currency-value]").forEach((option) => {
      option.addEventListener("click", (e) => {
        e.preventDefault();
        this.switchTo(option.dataset.currencyValue);
        // Close the dropdown
        const menu = switcher.querySelector("[data-dropdown-menu]");
        if (menu) menu.classList.add("hidden");
        switcher.querySelector("svg")?.classList.remove("rotate-180");
      });
    });

    this.updateToggleState();
  }

  static buildDropdownOptions(switcher) {
    const menu = switcher.querySelector("[data-dropdown-menu]");
    if (!menu) return;

    const locale = document.documentElement.lang || "fr";
    const localeCurrency = this.LOCALE_CURRENCY_MAP[locale] || "EUR";

    // Show only: EUR, USD, and locale's default currency
    const visibleCurrencies = ["EUR", "USD"];
    if (!visibleCurrencies.includes(localeCurrency)) {
      visibleCurrencies.push(localeCurrency);
    }

    const labels = {
      EUR: "€ EUR",
      USD: "$ USD",
      CAD: "$ CAD",
      GBP: "£ GBP",
      CHF: "CHF",
      AUD: "A$ AUD",
      SEK: "SEK",
      NOK: "NOK",
      DKK: "DKK",
      JPY: "¥ JPY",
      CNY: "¥ CNY",
      INR: "₹ INR",
    };

    // Clear existing options and rebuild
    menu.innerHTML = "";
    visibleCurrencies.forEach((currency) => {
      const option = document.createElement("a");
      option.href = "#";
      option.dataset.currencyValue = currency;
      option.className =
        "flex items-center gap-2 px-3 py-2 text-sm text-gray-300 hover:bg-gray-700 hover:text-white";
      option.textContent = labels[currency] || currency;
      menu.appendChild(option);
    });
  }

  static updateToggleState() {
    const switcher = document.querySelector("[data-currency-switcher]");
    if (!switcher) return;

    const label = switcher.querySelector("[data-currency-label]");
    if (label) {
      const labels = {
        EUR: "€ EUR",
        USD: "$ USD",
        CAD: "$ CAD",
        GBP: "£ GBP",
        CHF: "CHF",
        AUD: "A$ AUD",
        SEK: "SEK",
        NOK: "NOK",
        DKK: "DKK",
        JPY: "¥ JPY",
        CNY: "¥ CNY",
        INR: "₹ INR",
      };
      label.textContent = labels[this.currentCurrency] || this.currentCurrency;
    }

    // Update active state in dropdown
    switcher.querySelectorAll("[data-currency-value]").forEach((option) => {
      if (option.dataset.currencyValue === this.currentCurrency) {
        option.classList.add("bg-gray-700");
      } else {
        option.classList.remove("bg-gray-700");
      }
    });
  }
}

// Auto-init on non-FR pages when DOM is ready
if (typeof document !== "undefined") {
  document.addEventListener("DOMContentLoaded", () => {
    const lang = document.documentElement.lang || "fr";
    if (lang !== "fr") {
      CurrencySwitcher.init();
    }
  });
}

export default CurrencySwitcher;
